import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { Empty, PageHead, Severity, Skel } from '../components/ui'
import { thaiDateTime } from '../lib/format'

type ChannelKind = 'webhook' | 'email' | 'line' | 'slack'
type Level = 'low' | 'medium' | 'high' | 'critical'

interface Channel {
  id: string
  kind: ChannelKind
  target: string
  events: string[]
  severity_min: Level
  enabled: boolean
  last_delivery_at?: string
  created_at: string
}

interface CreatedChannel {
  channel: Channel
  secret: string
}

const kindLabel: Record<ChannelKind, string> = {
  webhook: 'Webhook', email: 'อีเมล', line: 'LINE', slack: 'Slack',
}

const kindHint: Record<ChannelKind, string> = {
  webhook: 'https://example.com/hooks/audit — รับ JSON พร้อมลายเซ็น HMAC',
  slack: 'https://hooks.slack.com/services/… — ส่งเป็นข้อความเข้ากล่อง Slack',
  email: 'audit-ops@example.com — บันทึกเป็นคิวในระบบ (ยังไม่ส่งจริง)',
  line: 'รหัสผู้ใช้หรือกลุ่ม LINE — บันทึกเป็นคิวในระบบ (ยังไม่ส่งจริง)',
}

const severityLabel: Record<Level, string> = {
  low: 'ต่ำ', medium: 'ปานกลาง', high: 'สูง', critical: 'วิกฤต',
}

const eventOrder = ['finding.created', 'case.approved', 'schedule.run', 'whistle.received'] as const

const eventLabel: Record<string, string> = {
  'finding.created': 'พบข้อสังเกตใหม่',
  'case.approved': 'อนุมัติเคส',
  'schedule.run': 'รอบตรวจตามกำหนด',
  'whistle.received': 'รับแจ้งเบาะแส',
}

const stubKinds: ChannelKind[] = ['email', 'line']

export function Notifications() {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['notify-channels'], queryFn: () => api<Channel[]>('/v1/notifications/channels') })

  const [kind, setKind] = useState<ChannelKind>('webhook')
  const [target, setTarget] = useState('')
  const [events, setEvents] = useState<string[]>(['finding.created', 'case.approved'])
  const [severity, setSeverity] = useState<Level>('high')
  const [created, setCreated] = useState<CreatedChannel | null>(null)

  const create = useMutation({
    mutationFn: () => apiWrite<CreatedChannel>('/v1/notifications/channels', 'POST', {
      kind, target: target.trim(), events, severity_min: severity,
    }),
    onSuccess: (res) => {
      setCreated(res)
      setTarget('')
      void qc.invalidateQueries({ queryKey: ['notify-channels'] })
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => apiWrite<{ status: string }>(`/v1/notifications/channels/${id}`, 'DELETE'),
    onSuccess: (_res, id) => {
      if (created?.channel.id === id) setCreated(null)
      void qc.invalidateQueries({ queryKey: ['notify-channels'] })
    },
  })

  const toggleEvent = (event: string) =>
    setEvents((prev) => (prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event]))

  const rows = q.data ?? []
  const canSubmit = target.trim().length > 0 && events.length > 0 && !create.isPending

  return (
    <>
      <PageHead title="การแจ้งเตือน" sub="ส่งเหตุการณ์สำคัญออกไปยังช่องทางที่ทีมใช้อยู่จริง" />

      <div className="grid-main">
        <div className="card card-pad">
          <div className="card-label">เพิ่มช่องทาง</div>
          <p className="dim" style={{ fontSize: 12.5, marginTop: 10, lineHeight: 1.7 }}>
            เลือกได้ว่าจะรับเหตุการณ์ใดและตั้งแต่ระดับความรุนแรงใดขึ้นไป ช่องทางที่ระดับต่ำกว่าจะไม่ได้รับเหตุการณ์นั้น
          </p>

          <div className="row" style={{ gap: 12, marginTop: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="field" style={{ maxWidth: 170 }}>
              <label htmlFor="notify-kind">ช่องทาง</label>
              <select id="notify-kind" className="select" value={kind} onChange={(e) => setKind(e.target.value as ChannelKind)}>
                {(Object.keys(kindLabel) as ChannelKind[]).map((k) => (
                  <option key={k} value={k}>{kindLabel[k]}</option>
                ))}
              </select>
            </div>
            <div className="field" style={{ flex: 1, minWidth: 240 }}>
              <label htmlFor="notify-target">ปลายทาง</label>
              <input id="notify-target" className="input" value={target} placeholder={kindHint[kind]}
                onChange={(e) => setTarget(e.target.value)} />
            </div>
            <div className="field" style={{ maxWidth: 150 }}>
              <label htmlFor="notify-severity">ระดับขั้นต่ำ</label>
              <select id="notify-severity" className="select" value={severity} onChange={(e) => setSeverity(e.target.value as Level)}>
                {(Object.keys(severityLabel) as Level[]).map((s) => (
                  <option key={s} value={s}>{severityLabel[s]}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="field" style={{ marginTop: 14 }}>
            <label>เหตุการณ์ที่รับ</label>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              {eventOrder.map((event) => (
                <label key={event} className={`chip${events.includes(event) ? ' chip-on' : ''}`} style={{ cursor: 'pointer' }}>
                  <input type="checkbox" checked={events.includes(event)} onChange={() => toggleEvent(event)}
                    style={{ width: 12, height: 12, accentColor: 'var(--color-gold)' }} />
                  {eventLabel[event]}
                </label>
              ))}
            </div>
          </div>

          {stubKinds.includes(kind) ? (
            <div className="dim" style={{ fontSize: 11.5, marginTop: 12 }}>
              ช่องทางนี้ยังไม่เชื่อมต่อผู้ให้บริการ — ระบบจะบันทึกเป็นคิวและอัปเดตเวลาส่งล่าสุดให้เห็นในตาราง
            </div>
          ) : null}

          {create.isError ? (
            <div className="form-error">{create.error instanceof Error ? create.error.message : 'เพิ่มช่องทางไม่สำเร็จ'}</div>
          ) : null}

          <button className="btn btn-primary" style={{ marginTop: 16 }} disabled={!canSubmit} onClick={() => create.mutate()}>
            {create.isPending ? 'กำลังบันทึก…' : 'เพิ่มช่องทาง'}
          </button>
        </div>

        <div className="card card-pad">
          <div className="card-label">การส่งออก</div>
          <ul className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, margin: '12px 0 0', paddingLeft: 18 }}>
            <li>Webhook และ Slack ส่งทันทีพร้อมลายเซ็น HMAC-SHA256 ในหัวข้อ X-AuditAI-Signature</li>
            <li>ทุกช่องทางมี secret เฉพาะ ใช้ตรวจว่าคำขอมาจาก Audit AI จริง</li>
            <li>อีเมลและ LINE เป็นคิวบันทึกไว้ก่อน จะเชื่อมผู้ให้บริการในรอบถัดไป</li>
            <li>ส่งช้าไม่เกิน 5 วินาทีต่อช่องทาง และไม่ทำให้การตรวจสอบหลักล้มเหลว</li>
          </ul>
        </div>
      </div>

      {created ? (
        <div className="card card-pad" style={{ marginTop: 18 }}>
          <div className="row">
            <span className="card-label">secret สำหรับตรวจลายเซ็น</span>
            <span className="chip gold" style={{ marginLeft: 'auto' }}>แสดงครั้งเดียว</span>
          </div>
          <div className="mono" style={{ fontSize: 13, marginTop: 10, padding: '12px 14px', wordBreak: 'break-all',
            border: '1px solid var(--color-line-strong)', borderRadius: 6 }}>
            {created.secret}
          </div>
          <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>
            เก็บไว้ในระบบปลายทางแล้วปิดข้อความนี้ — ระบบไม่แสดง secret นี้อีก
          </div>
        </div>
      ) : null}

      <div className="card-label" style={{ marginTop: 26, marginBottom: 10 }}>ช่องทางที่ตั้งไว้</div>

      {q.isLoading ? <Skel h={180} /> : rows.length === 0 ? (
        <div className="card">
          <Empty title="ยังไม่มีช่องทางแจ้งเตือน" body="เพิ่มช่องทางแรกด้านบน เพื่อให้ทีมได้รับข้อสังเกตและเคสที่อนุมัติแล้ว" />
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>ช่องทาง</th><th>ปลายทาง</th><th>เหตุการณ์</th><th>ระดับขั้นต่ำ</th>
                <th>สถานะ</th><th>ส่งล่าสุด</th><th />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} style={{ cursor: 'default' }}>
                  <td><span className="chip">{kindLabel[c.kind]}</span></td>
                  <td className="mono" style={{ fontSize: 11.5, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.target}
                  </td>
                  <td>
                    <span className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
                      {c.events.map((event) => (
                        <span key={event} className="chip" style={{ fontSize: 10.5 }}>{eventLabel[event] ?? event}</span>
                      ))}
                    </span>
                  </td>
                  <td><Severity value={c.severity_min} /></td>
                  <td>
                    <span className={`chip${c.enabled ? ' gold' : ''}`}>{c.enabled ? 'เปิดใช้งาน' : 'ปิดอยู่'}</span>
                  </td>
                  <td className="dim" style={{ fontSize: 11.5 }}>
                    {c.last_delivery_at ? thaiDateTime(c.last_delivery_at) : 'ยังไม่เคยส่ง'}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="btn btn-sm btn-ghost" disabled={remove.isPending && remove.variables === c.id}
                      onClick={() => remove.mutate(c.id)}>
                      ลบ
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="dim" style={{ fontSize: 11.5, marginTop: 14 }}>
        เหตุการณ์ที่รองรับ: {eventOrder.map((e) => eventLabel[e]).join(' · ')}
      </div>
    </>
  )
}
